import React, { useState, useEffect } from 'react'
import {
  BookOpen, ChevronRight, CheckCircle, Lightbulb,
  Play, Target, Brain, ArrowRight, Star, AlertTriangle,
  FileText, PenTool, Youtube,
  TrendingUp, Flame, ChevronDown, X,
  MessageCircle, Sparkles, Radical
} from 'lucide-react'
import { useProgressStore, useLearnProgress, useUnitProgress } from '../store/progressStore'
import { useVideoStore } from '../store/videoStore'
import ConceptVideoModal from '../components/ConceptVideoModal'
import {
  computePredictedFragments, FRAGMENT_TAG_CONFIG,
  type PredictedFragment
} from '../store/predictedFragments'

// ── 스토리텔링 인트로 데이터 ─────────────────────────────────────────────────
interface StoryLine {
  speaker: 'student' | 'teacher' | 'friend' | 'narrator'
  emoji: string
  name: string
  text: string
}
interface StepStory {
  scene: string
  lines: StoryLine[]
  reveal: string
  revealEmoji: string
}

const STEP_STORIES: Record<string, StepStory> = {
  NQ01: {
    scene: '🏗️ 건축 수업. 선생님이 정사각형 타일 넓이를 보여준다.',
    lines: [
      { speaker: 'teacher', emoji: '👩‍🏫', name: '선생님', text: '넓이가 9cm²인 정사각형의 한 변의 길이는 얼마일까요?' },
      { speaker: 'student', emoji: '🙋', name: '민준', text: '3cm요! 3×3=9니까요.' },
      { speaker: 'teacher', emoji: '👩‍🏫', name: '선생님', text: '맞아요! 그럼 넓이가 2cm²인 정사각형의 한 변은요?' },
      { speaker: 'friend', emoji: '🤔', name: '소연', text: '1.4...? 딱 떨어지지 않는 것 같은데요.' },
      { speaker: 'teacher', emoji: '👩‍🏫', name: '선생님', text: '바로 그 수가 \"루트 2(√2)\"예요! 제곱해서 2가 되는 수. 이게 제곱근이에요.' },
      { speaker: 'narrator', emoji: '💡', name: '', text: '제곱근: x² = a 일 때 x를 a의 제곱근이라 한다. √9 = 3, √2 ≈ 1.414...' },
    ],
    reveal: 'a의 제곱근: 제곱하면 a가 되는 수 | √a = a의 양의 제곱근 | 9의 제곱근 = ±3',
    revealEmoji: '√',
  },
  NQ02: {
    scene: '📐 수학 동아리. √의 성질을 탐구하는 날.',
    lines: [
      { speaker: 'student', emoji: '🙋', name: '민준', text: '선생님, (√5)²은 5인가요? 루트랑 제곱이 만나면 어떻게 되나요?' },
      { speaker: 'teacher', emoji: '👩‍🏫', name: '선생님', text: '(√5)² = 5예요. √와 ²은 서로 역연산이라 사라지죠. 반대로 √(5²) = √25 = 5예요!' },
      { speaker: 'friend', emoji: '😮', name: '소연', text: '그럼 √(-3)²은요? 음수도 제곱근이 있어요?' },
      { speaker: 'teacher', emoji: '👩‍🏫', name: '선생님', text: '√((-3)²) = √9 = 3이에요. 루트 안에 넣으면 항상 양수! √(a²) = |a|예요.' },
      { speaker: 'student', emoji: '😄', name: '민준', text: '아! 절댓값이 나오는군요. 음수라도 제곱하면 양수가 되니까요.' },
      { speaker: 'narrator', emoji: '💡', name: '', text: '핵심 성질: (√a)² = a (a≥0), √(a²) = |a|. 이 두 가지만 기억하면 대부분 해결!' },
    ],
    reveal: '(√a)² = a | √(a²) = |a| | √a × √b = √(ab) | √a ÷ √b = √(a/b)',
    revealEmoji: '📐',
  },
  NQ03: {
    scene: '🌀 수 체계의 지도를 그리는 수업.',
    lines: [
      { speaker: 'teacher', emoji: '👩‍🏫', name: '선생님', text: '지금까지 배운 수의 종류를 정리해볼게요. 자연수, 정수, 유리수... 다음은 뭘까요?' },
      { speaker: 'student', emoji: '🙋', name: '민준', text: '분수로 나타낼 수 없는 수가 있나요? √2 같은 것은요?' },
      { speaker: 'teacher', emoji: '👩‍🏫', name: '선생님', text: '정확해요! √2는 어떤 분수 a/b로도 나타낼 수 없어요. 이런 수를 무리수라고 해요.' },
      { speaker: 'friend', emoji: '😮', name: '소연', text: '그럼 유리수 + 무리수를 합치면 더 큰 집합이 되겠네요?' },
      { speaker: 'teacher', emoji: '👩‍🏫', name: '선생님', text: '그게 바로 실수(Real Number)예요! 유리수와 무리수를 모두 포함하는 가장 큰 수의 세계죠.' },
      { speaker: 'narrator', emoji: '💡', name: '', text: '실수 = 유리수 ∪ 무리수. 수직선 위의 모든 점은 하나의 실수에 대응한다!' },
    ],
    reveal: '무리수: 분수로 나타낼 수 없는 수 (√2, π...) | 실수 = 유리수 + 무리수 | 수직선 = 실수 직선',
    revealEmoji: '🌀',
  },
  NQ04: {
    scene: '📊 실수의 크기를 비교하는 수학 퀴즈 대회.',
    lines: [
      { speaker: 'teacher', emoji: '👩‍🏫', name: '선생님', text: '문제! √5와 2.2 중 어느 쪽이 더 클까요?' },
      { speaker: 'student', emoji: '🤔', name: '민준', text: '√5... 5를 1과 4 사이에서 보면... 2²=4, 3²=9이니까 √5는 2와 3 사이! 2.2와 비교하려면...' },
      { speaker: 'friend', emoji: '😄', name: '소연', text: '양변을 제곱해보면 돼요! (√5)²=5, (2.2)²=4.84. 5>4.84이니까 √5 > 2.2!' },
      { speaker: 'teacher', emoji: '👩‍🏫', name: '선생님', text: '완벽해요! 양변이 양수일 때 제곱해서 비교하면 부등호 방향이 그대로예요.' },
      { speaker: 'student', emoji: '😮', name: '민준', text: '아, 그래서 √10과 π 비교도 π≈3.14, (3.14)²=9.8596, (√10)²=10이니까 √10 > π 이군요!' },
      { speaker: 'narrator', emoji: '💡', name: '', text: '실수 대소 비교: 양수끼리면 제곱해서 비교 | 수직선에서 오른쪽 = 더 큰 수' },
    ],
    reveal: '양수 a,b: a>b ⟺ a²>b² | 수직선에서 위치로 비교 | √n 의 정수 부분: n-1 < √n < n 사이 확인',
    revealEmoji: '📊',
  },
  NQ05: {
    scene: '✖️ 제곱근 계산을 처음 접하는 민준.',
    lines: [
      { speaker: 'student', emoji: '🙋', name: '민준', text: '선생님, √2 × √3은 어떻게 계산해요? √6이 되나요?' },
      { speaker: 'teacher', emoji: '👩‍🏫', name: '선생님', text: '맞아요! √2 × √3 = √(2×3) = √6. 루트끼리 곱하면 안의 수끼리 곱하면 돼요.' },
      { speaker: 'friend', emoji: '😄', name: '소연', text: '그럼 √12를 간단히 하면? 12 = 4×3이니까 √12 = √(4×3) = √4 × √3 = 2√3!' },
      { speaker: 'teacher', emoji: '👩‍🏫', name: '선생님', text: '완벽해요! 제곱수를 찾아서 루트 밖으로 꺼내는 게 핵심이에요.' },
      { speaker: 'student', emoji: '🤔', name: '민준', text: '나눗셈은요? √18 ÷ √2는?' },
      { speaker: 'narrator', emoji: '💡', name: '', text: '√a × √b = √(ab), √a ÷ √b = √(a/b). 제곱수는 루트 밖으로! √(4k) = 2√k' },
    ],
    reveal: '√a × √b = √(ab) | √a ÷ √b = √(a/b) | k√a × m√b = km√(ab) | 제곱수 빼내기: √(4×3)=2√3',
    revealEmoji: '✖️',
  },
  NQ06: {
    scene: '➕ 같은 루트끼리만 더할 수 있다는 것을 배우는 날.',
    lines: [
      { speaker: 'student', emoji: '🙋', name: '민준', text: '선생님, √2 + √3은 √5인가요?' },
      { speaker: 'teacher', emoji: '👩‍🏫', name: '선생님', text: '아니에요! √2 + √3은 더 이상 간단해지지 않아요. 숫자로 확인해볼까요? √2≈1.41, √3≈1.73이면 합은 약 3.14예요. √5≈2.24와 달라요!' },
      { speaker: 'friend', emoji: '😮', name: '소연', text: '그럼 언제 더하고 뺄 수 있어요?' },
      { speaker: 'teacher', emoji: '👩‍🏫', name: '선생님', text: '루트 안의 수가 같을 때만! 2√3 + 5√3 = 7√3. 동류항처럼 계수만 더해요.' },
      { speaker: 'student', emoji: '😄', name: '민준', text: '3√5 - √5 = 2√5겠네요! 계수 3-1=2. 마치 3x - x = 2x처럼요!' },
      { speaker: 'narrator', emoji: '💡', name: '', text: '제곱근 덧셈: 루트 안의 수가 같아야만 계수를 더함. 다르면 더 이상 간단히 안 됨!' },
    ],
    reveal: 'a√k + b√k = (a+b)√k | √2 + √3은 계산 불가 | 먼저 루트를 간단히 하고 같은 것끼리 모아라',
    revealEmoji: '➕',
  },
  NQ07: {
    scene: '😰 제곱근이 어렵다는 친구를 위한 처음부터 설명.',
    lines: [
      { speaker: 'friend', emoji: '😰', name: '다른 친구', text: '루트가 뭔지도 모르겠어. √2가 대체 뭐야?' },
      { speaker: 'student', emoji: '🙋', name: '민준', text: '제곱해서 2가 되는 수야. 1²=1, 2²=4니까 √2는 1과 2 사이의 수야! 약 1.41쯤 돼.' },
      { speaker: 'teacher', emoji: '👩‍🏫', name: '선생님', text: '핵심만 기억해요: √ = \"제곱의 반대\". √9=3 왜냐면 3²=9이니까. 이것만 알면 됩니다!' },
      { speaker: 'friend', emoji: '😮', name: '다른 친구', text: '오... 그래서 √25=5인 거구나! 5²=25이니까. 생각보다 어렵지 않네?' },
      { speaker: 'student', emoji: '😄', name: '민준', text: '맞아! √의 완전제곱수(1,4,9,16,25,36...)만 먼저 외우면 나머지도 쉬워져.' },
      { speaker: 'narrator', emoji: '💡', name: '', text: '√ = 제곱의 역연산. √n²=n, (√n)²=n. 완전제곱수부터 시작하면 두렵지 않아요!' },
    ],
    reveal: '√ = 제곱의 역연산 | √1=1, √4=2, √9=3, √16=4, √25=5 | 모르면 완전제곱수부터!',
    revealEmoji: '🌱',
  },
}

// ── 학습 단계 정의 ───────────────────────────────────────────────────────────
const STEPS = [
  {
    id: 'NQ01',
    title: '제곱근의 뜻과 표현',
    subtitle: '제곱근의 정의와 √ 기호를 이해한다',
    icon: '√',
    color: 'bg-blue-500',
    lightColor: 'bg-blue-50',
    borderColor: 'border-blue-300',
    textColor: 'text-blue-700',
    difficulty: '기초',
    difficultyColor: 'bg-green-100 text-green-700',
    tags: ['제곱근', '근호', '양의 제곱근'],
    objective: '제곱근의 뜻을 이해하고 근호(√)를 사용하여 나타낼 수 있다.',
    concept: `**제곱근(Square Root)**이란 어떤 수 x를 제곱하여 a가 될 때, x를 a의 제곱근이라 합니다.

📌 정의
• x² = a 일 때, x는 a의 제곱근
• a > 0이면 제곱근이 2개: +√a (양의 제곱근), -√a (음의 제곱근)
• a = 0이면 제곱근이 1개: 0
• a < 0이면 실수 범위에서 제곱근 없음

📌 근호(√) 표기
• √a: a의 양의 제곱근 (a ≥ 0)
• -√a: a의 음의 제곱근
• ±√a: a의 두 제곱근을 한꺼번에 표현

📌 완전제곱수의 제곱근
| a  | √a |
|----|-----|
| 1  | 1   |
| 4  | 2   |
| 9  | 3   |
| 16 | 4   |
| 25 | 5   |
| 36 | 6   |
| 49 | 7   |
| 64 | 8   |
| 81 | 9   |
| 100| 10  |

⚠️ 수포자 탈출 포인트
√9 = 3인 이유: \"제곱해서 9가 되는 양수\" → 3² = 9이므로 √9 = 3
√9 ≠ ±3. √9는 양의 제곱근 하나만 가리킵니다!`,
    keyPoints: [
      '제곱근: 제곱해서 a가 되는 수',
      '√a: a의 양의 제곱근 (a ≥ 0)',
      '0의 제곱근은 0 하나뿐',
      '음수의 제곱근은 실수 범위에서 없음',
    ],
    examples: [
      {
        question: '다음을 구하시오: ① 16의 제곱근  ② √49  ③ -√25',
        answer: '① ±4  ② 7  ③ -5',
        explanation: '① 4²=16, (-4)²=16이므로 제곱근은 ±4\n② √49 = 7 (7²=49의 양의 제곱근)\n③ -√25 = -5 (25의 음의 제곱근)',
      },
      {
        question: '넓이가 50cm²인 정사각형의 한 변의 길이는?',
        answer: '√50 = 5√2 ≈ 7.07 cm',
        explanation: '한 변 x라 하면 x² = 50이므로 x = √50 = √(25×2) = 5√2 (양수)',
      },
    ],
    practice: {
      question: '다음을 구하시오.\n① 64의 양의 제곱근  ② (-7)의 제곱근  ③ √(1/4)',
      answer: '① 8  ② 존재하지 않음 (실수 범위)  ③ 1/2',
      hint: '① 8²=64, ② 음수의 제곱근은 실수 범위에 없음, ③ (1/2)²=1/4',
      steps: [
        '① 8² = 64 → √64 = 8 (양의 제곱근)',
        '② -7 < 0이므로 실수 범위에서 제곱근 없음',
        '③ (1/2)² = 1/4 → √(1/4) = 1/2',
      ],
    },
    misconceptions: ['NM01', 'NM02'],
  },
  {
    id: 'NQ02',
    title: '제곱근의 성질',
    subtitle: '제곱근의 기본 성질을 이해하고 계산에 활용한다',
    icon: '📐',
    color: 'bg-purple-500',
    lightColor: 'bg-purple-50',
    borderColor: 'border-purple-300',
    textColor: 'text-purple-700',
    difficulty: '기초',
    difficultyColor: 'bg-green-100 text-green-700',
    tags: ['제곱근 성질', '절댓값', '근호 계산'],
    objective: '제곱근의 핵심 성질 (√a)²=a, √(a²)=|a|를 이해하고 적용한다.',
    concept: `**제곱근의 핵심 성질 4가지**

📌 성질 1: (√a)² = a  (a ≥ 0)
• (√5)² = 5,  (√3)² = 3
• \"루트와 제곱은 역연산\"

📌 성질 2: √(a²) = |a|  (모든 실수 a)
• √(3²) = |3| = 3
• √((-3)²) = √9 = 3 = |-3|
• \"루트 안에 제곱이 있으면 절댓값\"

📌 성질 3: √a × √b = √(ab)  (a,b ≥ 0)
• √2 × √3 = √6
• √4 × √9 = √36 = 6 = 2 × 3 ✓

📌 성질 4: √a ÷ √b = √(a/b)  (a ≥ 0, b > 0)
• √6 ÷ √3 = √2
• √12 ÷ √3 = √4 = 2

⚠️ 주의
• √a + √b ≠ √(a+b)
  (√4 + √9 = 2+3 = 5 ≠ √13)
• 항상 루트 밖으로 꺼낼 때 절댓값 확인!`,
    keyPoints: [
      '(√a)² = a (a ≥ 0)',
      '√(a²) = |a| (절댓값!)',
      '√a × √b = √(ab)',
      '√a ÷ √b = √(a/b)',
    ],
    examples: [
      {
        question: '다음을 계산하시오: ① (√7)²  ② √((-5)²)  ③ √3 × √12',
        answer: '① 7  ② 5  ③ 6',
        explanation: '① (√7)² = 7 (역연산)\n② √((-5)²) = √25 = 5 = |-5|\n③ √3 × √12 = √36 = 6',
      },
      {
        question: 'a = -2일 때, √(a²) + √((a+1)²)을 구하시오.',
        answer: '2 + 1 = 3',
        explanation: '√((-2)²) = |-2| = 2\n√((-2+1)²) = √((-1)²) = |-1| = 1\n→ 2 + 1 = 3',
      },
    ],
    practice: {
      question: '다음을 계산하시오.\n① √(4²)  ② (-√3)²  ③ √8 × √2',
      answer: '① 4  ② 3  ③ 4',
      hint: '① √(4²)=|4|=4, ② (-√3)²=(√3)²=3, ③ √8×√2=√16=4',
      steps: [
        '① √(4²) = |4| = 4',
        '② (-√3)² = (-1)² × (√3)² = 1 × 3 = 3',
        '③ √8 × √2 = √(8×2) = √16 = 4',
      ],
    },
    misconceptions: ['NM03', 'NM04'],
  },
  {
    id: 'NQ03',
    title: '무리수와 실수',
    subtitle: '유리수·무리수·실수의 체계를 완전히 이해한다',
    icon: '🌀',
    color: 'bg-emerald-500',
    lightColor: 'bg-emerald-50',
    borderColor: 'border-emerald-300',
    textColor: 'text-emerald-700',
    difficulty: '기초',
    difficultyColor: 'bg-green-100 text-green-700',
    tags: ['무리수', '실수', '수 체계'],
    objective: '무리수의 정의를 이해하고, 실수의 분류 체계를 설명할 수 있다.',
    concept: `**무리수(Irrational Number)**란 유리수가 아닌 실수, 즉 분수 a/b (b≠0, a·b 정수)로 나타낼 수 없는 수입니다.

📌 무리수의 예
• √2, √3, √5, √7 (완전제곱수가 아닌 수의 제곱근)
• π = 3.14159265...
• e = 2.71828...

📌 실수의 분류 체계
       실수(Real Numbers)
      /              \\
  유리수            무리수
  /    \\           (√2, π, e...)
정수   (분수)
 |
자연수

포함 관계: 자연수 ⊂ 정수 ⊂ 유리수 ⊂ 실수

📌 소수로 나타낼 때
• 유리수 → 유한소수 또는 순환소수
• 무리수 → 순환하지 않는 무한소수

📌 실수와 수직선
수직선 위의 모든 점은 하나의 실수에 대응하고,
모든 실수는 수직선 위의 한 점에 대응합니다.
(수직선 = 실수 직선)

⚠️ 중요
• √4 = 2 → 유리수 (2는 정수이므로)
• √2 → 무리수 (분수로 나타낼 수 없음)
• 분수 꼴이면 유리수, √n에서 n이 완전제곱수면 유리수!`,
    keyPoints: [
      '무리수: 분수로 나타낼 수 없는 수 (순환없는 무한소수)',
      '실수 = 유리수 + 무리수',
      '√n이 무리수가 되려면 n이 완전제곱수가 아니어야 함',
      '수직선 위의 점 ↔ 실수 (일대일 대응)',
    ],
    examples: [
      {
        question: '다음 수를 유리수, 무리수로 분류하시오: √9, √5, 0.3̇, π, -√16, 3/7',
        answer: '유리수: √9=3, 0.3̇=1/3, -√16=-4, 3/7\n무리수: √5, π',
        explanation: '√9=3 (정수), √16=4 (정수) → 유리수\n√5: 완전제곱수 아님 → 무리수\nπ: 분수 불가 → 무리수',
      },
      {
        question: '무리수가 아닌 것을 고르시오: ① √12  ② √0.04  ③ -√7  ④ √(9/4)',
        answer: '② √0.04 = 0.2, ④ √(9/4) = 3/2 → 유리수',
        explanation: '√0.04 = √(4/100) = 2/10 = 0.2 (유리수)\n√(9/4) = 3/2 (유리수)\n√12, -√7은 완전제곱수가 아님 → 무리수',
      },
    ],
    practice: {
      question: '다음 수 중에서 무리수의 개수를 구하시오.\n√25, √10, -3, π/2, 0.121121112..., √(4/9)',
      answer: '무리수: √10, π/2, 0.121121112... → 3개',
      hint: '√25=5, √(4/9)=2/3은 유리수. 순환하지 않는 무한소수, 완전제곱수 아닌 루트 확인.',
      steps: [
        '√25 = 5 → 유리수',
        '√10: 완전제곱수 아님 → 무리수',
        '-3: 정수 → 유리수',
        'π/2: π는 무리수 → 무리수',
        '0.121121112...: 순환없는 무한소수 → 무리수',
        '√(4/9) = 2/3 → 유리수',
        '무리수 3개 (√10, π/2, 0.121121112...)',
      ],
    },
    misconceptions: ['NM05', 'NM06'],
  },
  {
    id: 'NQ04',
    title: '실수의 대소 비교',
    subtitle: '제곱근이 포함된 실수의 크기를 비교한다',
    icon: '📊',
    color: 'bg-orange-500',
    lightColor: 'bg-orange-50',
    borderColor: 'border-orange-300',
    textColor: 'text-orange-700',
    difficulty: '중급',
    difficultyColor: 'bg-yellow-100 text-yellow-700',
    tags: ['대소 비교', '실수 수직선', '제곱 비교'],
    objective: '제곱근을 포함한 실수의 대소 관계를 여러 방법으로 비교할 수 있다.',
    concept: `**실수의 대소 비교 방법**

📌 방법 1: 양변을 제곱해서 비교 (가장 많이 사용)
a > 0, b > 0 일 때: a > b ⟺ a² > b²
예) √5와 2.2 비교:
• (√5)² = 5, (2.2)² = 4.84
• 5 > 4.84 → √5 > 2.2

📌 방법 2: 수직선에서 위치로 비교
• √n을 수직선에 표시: n-1 < √n < n (n이 정수 사이)
• 수직선 오른쪽 = 더 큰 수

📌 방법 3: √n의 정수 부분 먼저 파악
• √2 ≈ 1.41... (1 < √2 < 2)
• √5 ≈ 2.23... (2 < √5 < 3)
• √10 ≈ 3.16... (3 < √10 < 4)

📌 실수 대소 비교의 성질
a < b 이면:
• a + c < b + c
• ac < bc (c > 0 일 때)
• ac > bc (c < 0 일 때, 부등호 방향 바뀜!)

📌 a와 b 사이의 실수
a < b 일 때, a와 b 사이의 정수: a보다 크고 b보다 작은 정수들`,
    keyPoints: [
      '양수끼리: 제곱해서 비교 (부등호 유지)',
      '수직선: n < √(n²) < n+1 이면 √(n²) 사이 확인',
      'a-b의 부호로 비교 가능',
      '음수와 양수: 음수 < 양수 (항상)',
    ],
    examples: [
      {
        question: '다음 대소를 비교하시오: ① √7과 3  ② 2√3과 √13',
        answer: '① √7 < 3  ② 2√3 > √13',
        explanation: '① (√7)²=7, 3²=9. 7<9 → √7<3\n② 2√3=√(4×3)=√12. √12²=12, 13. 12<13 → √12<√13 → 2√3<√13? 아니, 2√3=√12이므로 √12와 √13 비교: 12<13 → 2√3 < √13\n(주의: 문제 재확인 필요. 2√3=√12≈3.46, √13≈3.61 → 2√3 < √13)',
      },
      {
        question: '√5와 √3+1의 대소를 비교하시오.',
        answer: '√5 > √3+1',
        explanation: '√5 ≈ 2.236, √3+1 ≈ 1.732+1 = 2.732? → 아니면 양변제곱:\n(√5)²=5, (√3+1)²=3+2√3+1=4+2√3\n4+2√3 vs 5 → 2√3 vs 1 → 4×3=12 vs 1 → 12>1 → 4+2√3 > 5 → 그러면 √3+1 > √5\n다시: √3+1 > √5',
      },
    ],
    practice: {
      question: '다음을 크기순으로 나열하시오.\n√6, 2, √3+1',
      answer: '2 < √6 < √3+1? 또는 확인: √6≈2.449, 2, √3+1≈2.732\n→ 2 < √6 < √3+1',
      hint: '각 수를 소수로 근삿값을 구하거나 제곱해서 비교하세요.',
      steps: [
        '√6 ≈ 2.449',
        '2 = 2.000',
        '√3+1 ≈ 1.732+1 = 2.732',
        '크기순: 2 < √6 < √3+1',
      ],
    },
    misconceptions: ['NM07'],
  },
  {
    id: 'NQ05',
    title: '제곱근의 곱셈·나눗셈',
    subtitle: '제곱근을 곱하고 나누며 간단히 변환한다',
    icon: '✖️',
    color: 'bg-red-500',
    lightColor: 'bg-red-50',
    borderColor: 'border-red-300',
    textColor: 'text-red-700',
    difficulty: '중급',
    difficultyColor: 'bg-yellow-100 text-yellow-700',
    tags: ['제곱근 곱셈', '분모의 유리화', '근호 간단히'],
    objective: '제곱근의 곱셈·나눗셈을 계산하고 분모를 유리화할 수 있다.',
    concept: `**제곱근의 곱셈·나눗셈**

📌 기본 규칙
• √a × √b = √(ab)
• √a ÷ √b = √(a/b) = √a/√b
• a√m × b√n = ab√(mn)
• a√m ÷ b√n = (a/b) × √(m/n)

📌 근호를 간단히 하기
제곱인수를 찾아 밖으로 꺼내기:
• √12 = √(4×3) = 2√3
• √75 = √(25×3) = 5√3
• √48 = √(16×3) = 4√3
• √200 = √(100×2) = 10√2

📌 분모의 유리화
분모에 √가 있으면 분모·분자에 같은 √를 곱해 제거:
• 1/√2 = √2/(√2×√2) = √2/2
• 3/√5 = 3√5/(√5×√5) = 3√5/5
• 6/√3 = 6√3/3 = 2√3

⚠️ 계산 순서
① 근호를 간단히 (제곱수 밖으로)
② 계수끼리, 루트끼리 곱하거나 나누기
③ 분모에 루트가 있으면 유리화`,
    keyPoints: [
      '√a × √b = √(ab)',
      '제곱인수 찾아 밖으로: √(n²k) = n√k',
      '분모 유리화: 1/√a = √a/a',
      '계수끼리, 루트끼리 따로 계산',
    ],
    examples: [
      {
        question: '계산하시오: ① √18 × √2  ② √24 ÷ √6  ③ 2/√3',
        answer: '① 6  ② 2  ③ 2√3/3',
        explanation: '① √18 × √2 = √36 = 6\n② √24 ÷ √6 = √(24/6) = √4 = 2\n③ 2/√3 = 2√3/(√3×√3) = 2√3/3',
      },
      {
        question: '√72를 간단히 하시오.',
        answer: '6√2',
        explanation: '√72 = √(36×2) = √36×√2 = 6√2\n(72 = 2³×3² = 4×18 = 36×2)',
      },
    ],
    practice: {
      question: '다음을 계산하시오.\n① 3√2 × 2√6  ② √45 ÷ √5  ③ 4/√8',
      answer: '① 6√12 = 12√3  ② 3  ③ √2/√2... 4/√8 = 4/(2√2) = 2/√2 = √2',
      hint: '① 계수끼리, 루트끼리. ② √(45/5)=√9=3. ③ √8=2√2',
      steps: [
        '① 3√2 × 2√6 = (3×2)√(2×6) = 6√12 = 6×2√3 = 12√3',
        '② √45 ÷ √5 = √(45/5) = √9 = 3',
        '③ √8 = 2√2이므로 4/(2√2) = 2/√2 = 2√2/2 = √2',
      ],
    },
    misconceptions: ['NM08', 'NM09'],
  },
  {
    id: 'NQ06',
    title: '제곱근의 덧셈·뺄셈',
    subtitle: '동류항처럼 같은 루트끼리 더하고 뺀다',
    icon: '➕',
    color: 'bg-teal-500',
    lightColor: 'bg-teal-50',
    borderColor: 'border-teal-300',
    textColor: 'text-teal-700',
    difficulty: '중급',
    difficultyColor: 'bg-yellow-100 text-yellow-700',
    tags: ['제곱근 덧셈', '동류 제곱근', '근호 계산'],
    objective: '제곱근의 덧셈·뺄셈 규칙을 이해하고 계산에 적용할 수 있다.',
    concept: `**제곱근의 덧셈·뺄셈**

📌 핵심 규칙
루트 안의 수가 같아야만 더하거나 뺄 수 있다!

• a√k + b√k = (a+b)√k
• a√k - b√k = (a-b)√k

📌 예시
• 2√3 + 5√3 = 7√3   ✓
• 4√5 - √5 = 3√5    ✓ (4√5 - 1√5)
• √2 + √3 = √2 + √3  (더 이상 간단히 안 됨)

📌 먼저 간단히 하고 더하기
• √12 + √27 = 2√3 + 3√3 = 5√3
  (√12 = 2√3, √27 = 3√3)
• √50 - √18 = 5√2 - 3√2 = 2√2
  (√50 = 5√2, √18 = 3√2)

📌 혼합 계산
• 분배법칙 활용:
  √3(√3 + √6) = (√3)² + √3×√6 = 3 + √18 = 3 + 3√2
• 곱셈→덧셈 순서 지키기

⚠️ 절대 하면 안 되는 실수
• √4 + √9 = √(4+9) = √13  ← 틀림!
• √4 + √9 = 2 + 3 = 5       ← 맞음!`,
    keyPoints: [
      '루트 안의 수가 같아야 더하거나 뺄 수 있음',
      'a√k + b√k = (a+b)√k',
      '먼저 근호를 간단히 하고 계산',
      '√a + √b ≠ √(a+b) ← 절대 안 됨!',
    ],
    examples: [
      {
        question: '계산하시오: ① 3√2 + 5√2 - 2√2  ② √8 + √18',
        answer: '① 6√2  ② 5√2',
        explanation: '① (3+5-2)√2 = 6√2\n② √8=2√2, √18=3√2 → 2√2+3√2=5√2',
      },
      {
        question: '√12 - √48 + √75를 간단히 하시오.',
        answer: '√3',
        explanation: '√12=2√3, √48=4√3, √75=5√3\n→ 2√3 - 4√3 + 5√3 = (2-4+5)√3 = 3√3\n(다시: 3√3이 정답)',
      },
    ],
    practice: {
      question: '다음을 계산하시오.\n① 2√5 - 3√5 + √5  ② √20 + √45  ③ √6(√6 - √2)',
      answer: '① 0  ② 5√5  ③ 6 - √12 = 6 - 2√3',
      hint: '② √20=2√5, √45=3√5. ③ 분배법칙 적용',
      steps: [
        '① (2-3+1)√5 = 0',
        '② √20=2√5, √45=3√5 → 2√5+3√5=5√5',
        '③ √6×√6 - √6×√2 = 6 - √12 = 6 - 2√3',
      ],
    },
    misconceptions: ['NM10', 'NM11'],
  },
  {
    id: 'NQ07',
    title: '수포자를 위한 처음부터',
    subtitle: '제곱근을 가장 쉬운 방법으로 이해한다',
    icon: '🌱',
    color: 'bg-pink-500',
    lightColor: 'bg-pink-50',
    borderColor: 'border-pink-300',
    textColor: 'text-pink-700',
    difficulty: '기초',
    difficultyColor: 'bg-green-100 text-green-700',
    tags: ['기초부터', '자신감', '완전제곱수'],
    objective: '제곱근의 핵심을 쉬운 언어로 이해하고 완전제곱수 계산에 자신감을 갖는다.',
    concept: `**제곱근이 어렵게 느껴지는 이유?**
① √ 기호 자체가 낯설다
② \"무리수\"라는 단어가 어렵다
③ 계산 규칙이 많아 보인다

**이것만 기억하세요!**

🌱 제곱근 = \"제곱의 역연산\"
• 3² = 9  →  √9 = 3
• 5² = 25 →  √25 = 5
• \"제곱해서 나오는 수\"의 역

🔢 완전제곱수 외우기 (이게 전부!)
1² = 1   → √1 = 1
2² = 4   → √4 = 2
3² = 9   → √9 = 3
4² = 16  → √16 = 4
5² = 25  → √25 = 5
6² = 36  → √36 = 6
7² = 49  → √49 = 7
8² = 64  → √64 = 8
9² = 81  → √81 = 9
10² = 100 → √100 = 10

🧮 간단한 계산 원리
• √a × √a = a (역연산이므로)
• √4 × √9 = 2 × 3 = 6 = √36 ✓

💪 포기하지 마세요!
제곱근은 완전제곱수만 알면
90%가 해결됩니다!`,
    keyPoints: [
      '√ = 제곱의 역연산 (제곱해서 나오는 수)',
      '완전제곱수 √1~√100 외우기',
      '(√a)² = a, √(a²) = a (a≥0)',
      '천천히 완전제곱수부터 시작!',
    ],
    examples: [
      {
        question: '완전제곱수 표를 보며 √144를 구하시오.',
        answer: '√144 = 12 (12² = 144)',
        explanation: '12² = 144 이므로 √144 = 12\n(10²=100, 11²=121, 12²=144)',
      },
      {
        question: '다음을 계산하시오: ① √16 + √25  ② √9 × √4',
        answer: '① 4 + 5 = 9  ② 3 × 2 = 6',
        explanation: '완전제곱수를 알면 바로 계산 가능!\n√16=4, √25=5 → 9\n√9=3, √4=2 → 6',
      },
    ],
    practice: {
      question: '다음을 계산하시오. (완전제곱수 표를 참고하세요)\n① √81  ② √36 + √49  ③ √64 - √16',
      answer: '① 9  ② 6+7=13  ③ 8-4=4',
      hint: '9²=81, 6²=36, 7²=49, 8²=64, 4²=16',
      steps: [
        '① √81: 9² = 81 → √81 = 9',
        '② √36 + √49 = 6 + 7 = 13',
        '③ √64 - √16 = 8 - 4 = 4',
      ],
    },
    misconceptions: ['NM01', 'NM12'],
  },
]

const TOTAL_LEARN_STEPS = STEPS.length
const UNIT_ID = 'N2-2'

// ── 서브 컴포넌트 ────────────────────────────────────────────────────────────
function PracticeBox({ practice, stepId }: { practice: typeof STEPS[0]['practice'], stepId: string }) {
  const [showHint, setShowHint] = useState(false)
  const [showAnswer, setShowAnswer] = useState(false)
  const [showSteps, setShowSteps] = useState(false)
  return (
    <div className="mt-4 bg-indigo-50 border border-indigo-200 rounded-2xl p-4">
      <p className="text-xs font-bold text-indigo-600 mb-2 flex items-center gap-1">
        <PenTool size={12} /> 연습 문제
      </p>
      <p className="text-sm text-slate-800 font-medium mb-3 whitespace-pre-line">{practice.question}</p>
      <div className="flex flex-wrap gap-2">
        <button onClick={() => setShowHint(!showHint)}
          className="text-xs px-3 py-1.5 rounded-lg bg-amber-100 text-amber-700 hover:bg-amber-200 font-semibold transition-colors">
          💡 힌트
        </button>
        <button onClick={() => setShowSteps(!showSteps)}
          className="text-xs px-3 py-1.5 rounded-lg bg-blue-100 text-blue-700 hover:bg-blue-200 font-semibold transition-colors">
          📋 풀이 단계
        </button>
        <button onClick={() => setShowAnswer(!showAnswer)}
          className="text-xs px-3 py-1.5 rounded-lg bg-emerald-100 text-emerald-700 hover:bg-emerald-200 font-semibold transition-colors">
          ✅ 정답 확인
        </button>
      </div>
      {showHint && (
        <div className="mt-3 p-3 bg-amber-50 border border-amber-200 rounded-xl text-xs text-amber-800">
          💡 {practice.hint}
        </div>
      )}
      {showSteps && (
        <div className="mt-3 p-3 bg-blue-50 border border-blue-200 rounded-xl">
          <p className="text-xs font-bold text-blue-700 mb-2">단계별 풀이:</p>
          {practice.steps.map((s, i) => (
            <div key={i} className="flex gap-2 text-xs text-blue-800 mb-1">
              <span className="font-bold text-blue-500 w-4">{i + 1}.</span>
              <span className="font-mono">{s}</span>
            </div>
          ))}
        </div>
      )}
      {showAnswer && (
        <div className="mt-3 p-3 bg-emerald-50 border border-emerald-200 rounded-xl text-xs text-emerald-800 font-mono whitespace-pre-line">
          {practice.answer}
        </div>
      )}
    </div>
  )
}

function ExampleCard({ example, idx }: { example: typeof STEPS[0]['examples'][0], idx: number }) {
  const [open, setOpen] = useState(false)
  return (
    <div className="border border-slate-200 rounded-xl overflow-hidden">
      <button onClick={() => setOpen(!open)}
        className="w-full flex items-center justify-between px-4 py-3 bg-slate-50 hover:bg-slate-100 transition-colors text-left">
        <span className="text-sm font-semibold text-slate-700">예제 {idx + 1}: {example.question.slice(0, 28)}...</span>
        <ChevronDown size={14} className={`text-slate-400 transition-transform ${open ? 'rotate-180' : ''}`} />
      </button>
      {open && (
        <div className="px-4 py-3 space-y-2">
          <p className="text-sm text-slate-800 whitespace-pre-line">{example.question}</p>
          <div className="bg-emerald-50 rounded-lg p-3">
            <p className="text-xs font-bold text-emerald-600 mb-1">풀이</p>
            <p className="text-sm font-mono text-emerald-800 whitespace-pre-line">{example.answer}</p>
          </div>
          <p className="text-xs text-slate-500 italic whitespace-pre-line">{example.explanation}</p>
        </div>
      )}
    </div>
  )
}

function StoryCard({ story, stepId }: { story: StepStory, stepId: string }) {
  const [lineIdx, setLineIdx] = useState(0)
  const [revealed, setRevealed] = useState(false)
  const speakerColor: Record<string, string> = {
    student: 'bg-blue-100 text-blue-800',
    teacher: 'bg-purple-100 text-purple-800',
    friend: 'bg-green-100 text-green-800',
    narrator: 'bg-amber-100 text-amber-800',
  }
  return (
    <div className="bg-gradient-to-b from-slate-50 to-white border border-slate-200 rounded-2xl p-4">
      <p className="text-xs text-slate-500 italic mb-3">{story.scene}</p>
      <div className="min-h-[80px] mb-3">
        {story.lines.slice(0, lineIdx + 1).map((line, i) => (
          <div key={i} className={`flex gap-2 mb-2 ${i < lineIdx ? 'opacity-50' : ''}`}>
            <span className="text-lg flex-shrink-0">{line.emoji}</span>
            <div>
              {line.name && (
                <span className={`text-xs font-bold px-1.5 py-0.5 rounded mr-1 ${speakerColor[line.speaker]}`}>
                  {line.name}
                </span>
              )}
              <span className="text-sm text-slate-700">{line.text}</span>
            </div>
          </div>
        ))}
      </div>
      <div className="flex items-center gap-2 mb-3">
        <div className="flex-1 h-1 bg-slate-200 rounded-full">
          <div className="h-1 bg-indigo-400 rounded-full transition-all"
            style={{ width: `${((lineIdx + 1) / story.lines.length) * 100}%` }} />
        </div>
        <span className="text-xs text-slate-400">{lineIdx + 1}/{story.lines.length}</span>
      </div>
      <div className="flex gap-2">
        {lineIdx < story.lines.length - 1 ? (
          <button onClick={() => setLineIdx(i => i + 1)}
            className="flex-1 py-2 rounded-xl bg-indigo-500 hover:bg-indigo-600 text-white text-xs font-bold transition-colors flex items-center justify-center gap-1">
            <ChevronRight size={13} /> 다음
          </button>
        ) : (
          <button onClick={() => setRevealed(true)}
            className="flex-1 py-2 rounded-xl bg-emerald-500 hover:bg-emerald-600 text-white text-xs font-bold transition-colors flex items-center justify-center gap-1">
            <Sparkles size={13} /> 핵심 개념 보기
          </button>
        )}
        <button onClick={() => { setLineIdx(0); setRevealed(false) }}
          className="px-3 py-2 rounded-xl border border-slate-200 text-xs text-slate-500 hover:bg-slate-50 transition-colors">
          처음
        </button>
      </div>
      {revealed && (
        <div className="mt-3 p-3 bg-indigo-50 border border-indigo-200 rounded-xl flex items-start gap-2">
          <span className="text-xl">{story.revealEmoji}</span>
          <p className="text-xs text-indigo-800 font-medium leading-relaxed">{story.reveal}</p>
        </div>
      )}
    </div>
  )
}

function PredictedFragmentPanel({ fragments }: { fragments: PredictedFragment[] }) {
  const [dismissed, setDismissed] = useState<Set<string>>(new Set())
  const visible = fragments.filter(f => !dismissed.has(f.fragment.id))
  if (visible.length === 0) return null
  return (
    <div className="card border border-amber-200 bg-amber-50">
      <div className="flex items-center gap-2 mb-3">
        <Sparkles size={14} className="text-amber-500" />
        <p className="text-sm font-bold text-amber-800">AI 예상 파편</p>
        <span className="ml-auto text-xs bg-amber-200 text-amber-800 px-2 py-0.5 rounded-full font-bold">
          {visible.length}개
        </span>
      </div>
      <div className="space-y-2">
        {visible.map(f => {
          const cfg = FRAGMENT_TAG_CONFIG[f.tag] ?? FRAGMENT_TAG_CONFIG['review']
          return (
            <div key={f.fragment.id} className={`flex items-start gap-2 p-2 rounded-xl border ${cfg.color}`}>
              <span className="text-base">{f.fragment.emoji}</span>
              <div className="flex-1 min-w-0">
                <p className={`text-xs font-bold ${cfg.color}`}>{f.fragment.title}</p>
                <p className="text-xs text-slate-500 mt-0.5 truncate">{f.fragment.misconceptionLabel}</p>
              </div>
              <button onClick={() => setDismissed(s => new Set([...s, f.fragment.id]))}
                className="text-slate-300 hover:text-slate-500 flex-shrink-0">
                <X size={12} />
              </button>
            </div>
          )
        })}
      </div>
    </div>
  )
}

// ── 메인 컴포넌트 ────────────────────────────────────────────────────────────
interface N4LearnPageProps {
  defaultStepId?: string
}

export default function N4LearnPage({ defaultStepId }: N4LearnPageProps) {
  const [selectedStep, setSelectedStep] = useState<typeof STEPS[0] | null>(
    defaultStepId ?
      STEPS.find(s => s.id === defaultStepId) ?? null
      : null
  )
  const [activeTab, setActiveTab] = useState<'story' | 'concept' | 'examples' | 'practice'>('story')
  const [videoModal, setVideoModal] = useState<{ stepId: string; label: string } | null>(null)

  const { completeStep, markPracticeAnswered } = useProgressStore(s => ({
    completeStep: s.completeStep,
    markPracticeAnswered: s.markPracticeAnswered,
  }))
  const unitProgress = useUnitProgress(UNIT_ID)
  const learnProgress = useLearnProgress(UNIT_ID)
  const predictedFragments = computePredictedFragments(unitProgress, 3)
  const getApprovedByTag = useVideoStore(s => s.getApprovedByTag)

  const completedCount = learnProgress.completedCount
  const progressPct = learnProgress.pct

  useEffect(() => {
    if (defaultStepId) {
      const step = STEPS.find(s => s.id === defaultStepId)
      if (step) { setSelectedStep(step); setActiveTab('concept') }
    }
  }, [defaultStepId])

  const handleComplete = (step: typeof STEPS[0]) => {
    completeStep(UNIT_ID, step.id, step.title)
    setSelectedStep(null)
  }

  // ── 단계 목록 ──────────────────────────────────────────────────────────────
  if (!selectedStep) {
    return (
      <div className="p-4 space-y-4">
        {/* 헤더 */}
        <div className="card bg-gradient-to-br from-blue-50 to-indigo-50 border border-blue-200">
          <div className="flex items-center gap-3 mb-3">
            <div className="w-12 h-12 rounded-2xl bg-gradient-to-br from-blue-500 to-indigo-600 flex items-center justify-center">
              <span className="text-white text-xl font-black">√</span>
            </div>
            <div>
              <h2 className="text-lg font-black text-slate-800">제곱근과 실수</h2>
              <p className="text-xs text-slate-500">N2-2 · 중학교 2학년 수와 연산</p>
            </div>
          </div>
          {/* 진도 바 */}
          <div className="space-y-1">
            <div className="flex justify-between text-xs text-slate-500">
              <span>학습 진도</span>
              <span className="font-bold text-blue-600">{completedCount}/{TOTAL_LEARN_STEPS} 완료</span>
            </div>
            <div className="h-2.5 bg-blue-100 rounded-full overflow-hidden">
              <div className="h-2.5 bg-gradient-to-r from-blue-400 to-indigo-500 rounded-full transition-all duration-500"
                style={{ width: `${progressPct}%` }} />
            </div>
          </div>
        </div>

        {/* AI 예상 파편 */}
        <PredictedFragmentPanel fragments={predictedFragments} />

        {/* 단계 목록 */}
        <div className="space-y-3">
          {STEPS.map((step, idx) => {
            const sp = learnProgress[step.id]
            const isDone = sp?.status === 'completed' || sp?.status === 'mastered'
            const video = getApprovedByTag(step.id)
            return (
              <div key={step.id}
                className={`card border-2 cursor-pointer transition-all hover:shadow-md ${isDone ? 'border-emerald-200 bg-emerald-50' : 'border-slate-200 hover:border-blue-200'}`}
                onClick={() => { setSelectedStep(step); setActiveTab('story') }}>
                <div className="flex items-center gap-3">
                  {/* 아이콘 */}
                  <div className={`w-11 h-11 rounded-xl flex items-center justify-center flex-shrink-0 ${isDone ? 'bg-emerald-100' : step.lightColor}`}>
                    {isDone
                      ? <CheckCircle size={22} className="text-emerald-500" />
                      : <span className="text-xl">{step.icon}</span>}
                  </div>
                  {/* 정보 */}
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 mb-0.5">
                      <span className={`text-xs font-bold px-2 py-0.5 rounded-full ${step.difficultyColor}`}>
                        {step.difficulty}
                      </span>
                      {video && (
                        <span className="text-xs bg-red-100 text-red-600 px-2 py-0.5 rounded-full font-bold flex items-center gap-1">
                          <Youtube size={10} /> 영상
                        </span>
                      )}
                    </div>
                    <p className={`text-sm font-bold ${isDone ? 'text-emerald-700' : 'text-slate-800'}`}>
                      {idx + 1}. {step.title}
                    </p>
                    <p className="text-xs text-slate-400 truncate">{step.subtitle}</p>
                  </div>
                  <ChevronRight size={16} className="text-slate-400 flex-shrink-0" />
                </div>
                {/* 태그 */}
                <div className="flex gap-1.5 mt-2 flex-wrap">
                  {step.tags.map(t => (
                    <span key={t} className="text-xs bg-slate-100 text-slate-500 px-2 py-0.5 rounded-full">{t}</span>
                  ))}
                </div>
              </div>
            )
          })}
        </div>
      </div>
    )
  }

  // ── 단계 상세 ──────────────────────────────────────────────────────────────
  const story = STEP_STORIES[selectedStep.id]
  const sp = learnProgress[selectedStep.id]
  const isDone = sp?.status === 'completed' || sp?.status === 'mastered'
  const video = getApprovedByTag(selectedStep.id)

  const tabs = [
    { key: 'story' as const, label: '스토리', icon: <MessageCircle size={13} /> },
    { key: 'concept' as const, label: '개념', icon: <BookOpen size={13} /> },
    { key: 'examples' as const, label: '예제', icon: <FileText size={13} /> },
    { key: 'practice' as const, label: '연습', icon: <PenTool size={13} /> },
  ]

  return (
    <div className="p-4 space-y-4">
      {/* 뒤로 가기 */}
      <button onClick={() => setSelectedStep(null)}
        className="flex items-center gap-1 text-sm text-slate-500 hover:text-slate-700 transition-colors">
        ← 목록으로
      </button>

      {/* 단계 헤더 */}
      <div className={`card border-2 ${selectedStep.borderColor} ${selectedStep.lightColor}`}>
        <div className="flex items-start gap-3">
          <div className={`w-12 h-12 rounded-xl ${selectedStep.color} flex items-center justify-center flex-shrink-0`}>
            <span className="text-white text-xl">{selectedStep.icon}</span>
          </div>
          <div className="flex-1">
            <div className="flex items-center gap-2 mb-1">
              <span className={`text-xs font-bold px-2 py-0.5 rounded-full ${selectedStep.difficultyColor}`}>
                {selectedStep.difficulty}
              </span>
              <span className="text-xs text-slate-400">{selectedStep.id}</span>
              {isDone && <CheckCircle size={14} className="text-emerald-500" />}
            </div>
            <h3 className="text-base font-black text-slate-800">{selectedStep.title}</h3>
            <p className="text-xs text-slate-500 mt-0.5">{selectedStep.subtitle}</p>
          </div>
        </div>
        {/* 학습 목표 */}
        <div className="mt-3 p-3 bg-white/70 rounded-xl border border-white">
          <div className="flex items-center gap-1.5 mb-1">
            <Target size={12} className={selectedStep.textColor} />
            <p className="text-xs font-bold text-slate-600">학습 목표</p>
          </div>
          <p className="text-xs text-slate-700 leading-relaxed">{selectedStep.objective}</p>
        </div>
        {/* 영상 버튼 */}
        {video && (
          <button onClick={() => setVideoModal({ stepId: selectedStep.id, label: selectedStep.title })}
            className="mt-2 w-full flex items-center justify-center gap-2 py-2 rounded-xl bg-red-50 border border-red-200 text-red-600 text-xs font-bold hover:bg-red-100 transition-colors">
            <Youtube size={13} /> 개념 영상 보기
          </button>
        )}
      </div>

      {/* 탭 */}
      <div className="flex gap-1 bg-slate-100 rounded-xl p-1">
        {tabs.map(tab => (
          <button key={tab.key}
            onClick={() => setActiveTab(tab.key)}
            className={`flex-1 flex items-center justify-center gap-1.5 py-2 rounded-lg text-xs font-bold transition-all ${
              activeTab === tab.key
                ? 'bg-white text-slate-800 shadow-sm'
                : 'text-slate-500 hover:text-slate-700'
            }`}>
            {tab.icon}{tab.label}
          </button>
        ))}
      </div>

      {/* 탭 콘텐츠 */}
      {activeTab === 'story' && story && (
        <StoryCard story={story} stepId={selectedStep.id} />
      )}

      {activeTab === 'concept' && (
        <div className="card space-y-4">
          <div className="flex items-center gap-2 mb-1">
            <Brain size={15} className={selectedStep.textColor} />
            <h4 className="text-sm font-bold text-slate-800">핵심 개념</h4>
          </div>
          <div className="bg-slate-50 rounded-xl p-4 text-xs text-slate-700 leading-relaxed whitespace-pre-wrap font-mono">
            {selectedStep.concept}
          </div>
          {/* 키 포인트 */}
          <div className="space-y-2">
            <p className="text-xs font-bold text-slate-600 flex items-center gap-1">
              <Star size={12} className="text-amber-400" /> 핵심 포인트
            </p>
            {selectedStep.keyPoints.map((kp, i) => (
              <div key={i} className="flex items-start gap-2 p-2 bg-amber-50 rounded-lg border border-amber-100">
                <CheckCircle size={13} className="text-amber-500 flex-shrink-0 mt-0.5" />
                <p className="text-xs text-slate-700">{kp}</p>
              </div>
            ))}
          </div>
          {/* 오개념 경고 */}
          {selectedStep.misconceptions.length > 0 && (
            <div className="p-3 bg-red-50 border border-red-200 rounded-xl">
              <div className="flex items-center gap-1.5 mb-1.5">
                <AlertTriangle size={12} className="text-red-500" />
                <p className="text-xs font-bold text-red-600">흔한 오개념 주의</p>
              </div>
              <div className="flex flex-wrap gap-1">
                {selectedStep.misconceptions.map(code => (
                  <span key={code} className="text-xs bg-red-100 text-red-700 px-2 py-0.5 rounded-full font-mono">{code}</span>
                ))}
              </div>
            </div>
          )}
        </div>
      )}

      {activeTab === 'examples' && (
        <div className="space-y-3">
          {selectedStep.examples.map((ex, i) => (
            <ExampleCard key={i} example={ex} idx={i} />
          ))}
        </div>
      )}

      {activeTab === 'practice' && (
        <PracticeBox practice={selectedStep.practice} stepId={selectedStep.id} />
      )}

      {/* 완료 버튼 */}
      {!isDone ? (
        <button
          onClick={() => handleComplete(selectedStep)}
          className={`w-full py-3 ${selectedStep.color} hover:opacity-90 text-white font-bold rounded-2xl transition-all flex items-center justify-center gap-2 text-sm`}>
          <CheckCircle size={16} /> 이 단계 완료하기
        </button>
      ) : (
        <div className="w-full py-3 bg-emerald-100 text-emerald-700 font-bold rounded-2xl flex items-center justify-center gap-2 text-sm border border-emerald-200">
          <CheckCircle size={16} /> 완료한 단계입니다 ✓
        </div>
      )}

      {videoModal && (
        <ConceptVideoModal
          conceptTag={videoModal.stepId}
          conceptLabel={videoModal.label}
          onClose={() => setVideoModal(null)}
        />
      )}
    </div>
  )
}
